import type {
  ColumnData,
  ColumnProp,
  ColumnRegular,
  ColumnTemplateProp,
  HyperFunc,
  MultiFilterItem,
  VNode,
} from '@revolist/react-datagrid';

import {
  RevoGridColumnFilter,
  RevoGridColumnFilters,
  RevoGridFilterOperator,
  RevoGridTableExtras,
  RevoGridTableFilterConfig,
} from './types';

/**
 * The operators the panel offers, in display order.
 *
 * Each id is the name RevoGrid indexes its own condition function under, so the
 * value crosses the `filter` event unchanged — renaming one silently stops it
 * matching anything.
 */
export const REVO_GRID_FILTER_OPERATORS: readonly RevoGridFilterOperator[] = [
  'notEmpty',
  'empty',
  'eq',
  'notEq',
  'begins',
  'contains',
  'notContains',
];

/** Captions, matching RevoGrid's own wording for the same conditions. */
export const REVO_GRID_FILTER_OPERATOR_LABELS: Record<RevoGridFilterOperator, string> = {
  notEmpty: 'Set',
  empty: 'Not set',
  eq: 'Equal',
  notEq: 'Not equal',
  begins: 'Begins with',
  contains: 'Contains',
  notContains: 'Does not contain',
};

export const DEFAULT_REVO_GRID_FILTER_OPERATOR: RevoGridFilterOperator = 'contains';

const VALUELESS_OPERATORS: readonly RevoGridFilterOperator[] = ['notEmpty', 'empty'];

export const operatorNeedsValue = (operator: RevoGridFilterOperator): boolean =>
  !VALUELESS_OPERATORS.includes(operator);

export const isFilterApplied = (filter?: RevoGridColumnFilter): boolean =>
  !!filter && (!operatorNeedsValue(filter.operator) || filter.value !== '');

export const toMultiFilterItem = (filters: RevoGridColumnFilters): MultiFilterItem =>
  Object.entries(filters).reduce<MultiFilterItem>((items, [prop, filter], index) => {
    if (isFilterApplied(filter)) {
      items[prop] = [{ id: index, type: filter.operator, value: filter.value, relation: 'and' }];
    }
    return items;
  }, {});

export const getFilteredProps = (filters: RevoGridColumnFilters): Set<ColumnProp> =>
  new Set(Object.keys(filters).filter((prop) => isFilterApplied(filters[prop])));

export const resolveFilterConfig = (
  filterable: boolean | RevoGridTableFilterConfig | undefined
): Required<RevoGridTableFilterConfig> => {
  const config = typeof filterable === 'object' ? filterable : {};

  return {
    operators: config.operators ?? REVO_GRID_FILTER_OPERATORS,
    defaultOperator: config.defaultOperator ?? DEFAULT_REVO_GRID_FILTER_OPERATOR,
  };
};

export const REVO_GRID_HEADER_CONTENT_CLASS = 'sm-header-content';
export const REVO_GRID_HEADER_LABEL_CLASS = 'sm-header-label';
export const REVO_GRID_FILTER_BUTTON_CLASS = 'sm-filter-button';
export const REVO_GRID_FILTER_BUTTON_ACTIVE_CLASS = 'active';

/** RevoGrid's own funnel glyph, so the affordance stays the familiar one. */
const FUNNEL_PATH =
  'M43,48 L43,56 L21,56 L21,48 L43,48 Z M53,28 L53,36 L12,36 L12,28 L53,28 Z M64,8 L64,16 L0,16 L0,8 L64,8 Z';

/** `name` is `DataFormat<any>`, and a column may be labelled by a template. */
const getColumnLabel = (column: ColumnTemplateProp): string =>
  typeof column.name === 'string' && column.name ? column.name : String(column.prop);

const renderFilterButton = (
  h: HyperFunc<VNode>,
  column: ColumnTemplateProp,
  extra?: RevoGridTableExtras
) => {
  const isApplied = !!extra?.filteredProps?.has(column.prop) || !!column.hasFilter;
  const label = getColumnLabel(column);

  return h(
    'button',
    {
      type: 'button',
      class: `${REVO_GRID_FILTER_BUTTON_CLASS}${
        isApplied ? ` ${REVO_GRID_FILTER_BUTTON_ACTIVE_CLASS}` : ''
      }`,
      'aria-label': `Filter by ${label}`,
      'data-sm': `revo-grid-filter-button-${column.prop}`,
      // The header cell turns a mousedown into `columnclick` and a click into
      // `headerclick` → sort, so neither may reach it.
      onMouseDown: (event: MouseEvent) => event.stopPropagation(),
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        // the glyph is what gets clicked, so measure the button around it
        const button = (event.target as HTMLElement).closest(
          `.${REVO_GRID_FILTER_BUTTON_CLASS}`
        ) as HTMLElement | null;
        if (button) {
          extra?.onOpenFilter?.({
            prop: column.prop,
            name: label,
            anchor: button.getBoundingClientRect(),
          });
        }
      },
    },
    h(
      'svg',
      { viewBox: '0 0 64 64', 'aria-hidden': 'true' },
      h('path', { fill: 'currentColor', d: FUNNEL_PATH })
    )
  );
};

const withFilterButton =
  (columnTemplate: ColumnRegular['columnTemplate']) =>
  (h: HyperFunc<VNode>, column: ColumnTemplateProp, extra?: RevoGridTableExtras) =>
    h('span', { class: REVO_GRID_HEADER_CONTENT_CLASS }, [
      h(
        'span',
        { class: REVO_GRID_HEADER_LABEL_CLASS },
        columnTemplate ? columnTemplate(h, column, extra) : column.name
      ),
      renderFilterButton(h, column, extra),
    ]);

const toFilterableColumn = (column: ColumnRegular): ColumnRegular => {
  // a column that opted out keeps its plain header
  if (column.filter === false) {
    return column;
  }
  return {
    ...column,
    /**
     * MUST stay false. RevoGrid renders its own funnel whenever the plugin is on
     * and the column does not say otherwise, and that button opens the panel
     * this one replaces — so opting every column out is what leaves exactly one
     * filter affordance per header.
     */
    filter: false,
    columnTemplate: withFilterButton(column.columnTemplate),
  };
};

export const withFilterableColumns = (columns: ColumnData): ColumnData =>
  columns.map((column) =>
    'children' in column
      ? { ...column, children: withFilterableColumns(column.children) }
      : toFilterableColumn(column)
  );
